if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      // Dawn updates this input's value on variant changes; availability is guarded by the button.
      this.variantIdInput.disabled = false;
      this.submitButton = this.querySelector('[type="submit"][name="add"]');
      this.submitButtonText = this.submitButton.querySelector('span');
      this.buyNowButton = this.querySelector('[data-ec-buy-now]');
      this.hideErrors = this.dataset.hideErrors === 'true';
      // Preserve this store's existing add-then-open-cart behavior.
      this.cart = null;
      this.pending = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.observer = new MutationObserver(() => this.syncBuyNow());
      this.onPageShow = (event) => {
        if (!event.persisted) return;
        clearTimeout(this.navigationTimeout);
        this.pending = false;
        this.submitButton.removeAttribute('aria-disabled');
        for (const button of [this.submitButton, this.buyNowButton].filter(Boolean)) {
          button.removeAttribute('aria-busy');
          button.classList.remove('loading');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
        }
        this.syncBuyNow();
      };
    }

    connectedCallback() {
      window.addEventListener('pageshow', this.onPageShow);
      this.observer.observe(this.submitButton, {
        attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class']
      });
      this.syncBuyNow();
      this.styleFrame = requestAnimationFrame(() => {
        if (!this.buyNowButton) return;
        const style = getComputedStyle(this.submitButton);
        // Match the actual existing button, without guessing its gray hex value.
        if (style.backgroundColor !== 'rgba(0, 0, 0, 0)' && style.backgroundColor !== 'transparent') {
          this.buyNowButton.style.setProperty('--ec-buy-now-ink', style.backgroundColor);
        }
        for (const key of ['fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'textTransform', 'borderRadius']) {
          this.buyNowButton.style[key] = style[key];
        }
        this.buyNowButton.style.minHeight = style.height;
        this.buyNowButton.hidden = false;
      });
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.onPageShow);
      this.observer.disconnect();
      clearTimeout(this.navigationTimeout);
      cancelAnimationFrame(this.styleFrame);
    }

    syncBuyNow() {
      if (!this.buyNowButton) return;
      this.buyNowButton.disabled = this.pending || this.submitButton.disabled ||
        this.submitButton.getAttribute('aria-disabled') === 'true';
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.pending || this.submitButton.disabled ||
          this.submitButton.getAttribute('aria-disabled') === 'true') return;
      const buyNow = evt.submitter === this.buyNowButton && Boolean(this.buyNowButton);
      if (buyNow && window.Shopify.designMode) {
        this.handleErrorMessage('Please open this draft theme in a separate Preview tab to test Buy now. Checkout is not tested inside the theme editor.');
        return;
      }
      if (!this.form.reportValidity()) return;
      if (!this.variantIdInput || this.variantIdInput.disabled || !this.variantIdInput.value) {
        this.handleErrorMessage('Please select an available size, material and frame style.');
        return;
      }

      if (buyNow) {
        await this.buyCurrentSelection();
        return;
      }

      // Capture the selected variant, quantity, selling plan and line-item properties together.
      const formData = new FormData(this.form);
      formData.delete('ec_buy_now');
      this.handleErrorMessage();
      this.pending = true;
      this.error = false;
      const activeButton = buyNow ? this.buyNowButton : this.submitButton;
      this.submitButton.setAttribute('aria-disabled', 'true');
      activeButton.setAttribute('aria-busy', 'true');
      activeButton.classList.add('loading');
      activeButton.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.syncBuyNow();
      let navigating = false;
      let uncertain = false;
      const controller = new AbortController();
      const requestTimeout = setTimeout(() => controller.abort(), 20000);

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
          body: formData
        });
        const data = await response.json();
        clearTimeout(requestTimeout);
        if (!response.ok || data.status) {
          // A server error can occur after the cart mutation; don't blindly retry.
          if (response.status >= 500) throw new Error('Uncertain cart response');
          this.error = true;
          this.handleErrorMessage(typeof data.description === 'string' ? data.description :
            'This selection could not be added. Please check availability and quantity.');
          return;
        }
        window.location.assign(`${window.Shopify.routes.root}cart`);
        navigating = true;
        this.navigationTimeout = setTimeout(() => {
          this.pending = false;
          this.uncertain = true;
          this.submitButton.disabled = true;
          this.submitButton.removeAttribute('aria-disabled');
          activeButton.removeAttribute('aria-busy');
          activeButton.classList.remove('loading');
          activeButton.querySelector('.loading__spinner')?.classList.add('hidden');
          this.syncBuyNow();
          this.handleErrorMessage('Your item was added, but this page did not leave for checkout or the cart. Open your cart to continue without adding it again.');
          this.addCartRecoveryLink();
        }, 10000);
      } catch (error) {
        // Do not retry the POST automatically: it could duplicate an item.
        uncertain = true;
        this.handleErrorMessage('We could not confirm the result. Please open your cart to check whether this item was added before trying again.');
        this.submitButton.disabled = true;
        this.addCartRecoveryLink();
      } finally {
        clearTimeout(requestTimeout);
        if (!navigating) {
          this.pending = false;
          this.submitButton.removeAttribute('aria-disabled');
          activeButton.removeAttribute('aria-busy');
          activeButton.classList.remove('loading');
          activeButton.querySelector('.loading__spinner')?.classList.add('hidden');
          // An ambiguous network result stays locked until the page is reloaded.
          this.uncertain = uncertain;
          this.syncBuyNow();
        }
      }
    }

    async buyCurrentSelection() {
      const formData = new FormData(this.form);
      const variant = String(formData.get('id') || '');
      const quantity = Number(formData.get('quantity') || 1);
      if (!/^\d+$/.test(variant) || !Number.isInteger(quantity) || quantity < 1) {
        this.handleErrorMessage('Please select a valid product and quantity.');
        return;
      }
      const attributes = [];
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (!match || value === '') continue;
        if (typeof value !== 'string') {
          this.handleErrorMessage('Please use Add to cart for products requiring file uploads.');
          return;
        }
        attributes.push({ key: match[1], value });
      }
      const merchandiseId = `gid://shopify/ProductVariant/${variant}`;
      const line = { merchandiseId, quantity, attributes };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) line.sellingPlanId = `gid://shopify/SellingPlan/${sellingPlan}`;
      const input = { lines: [line] };
      if (/^[A-Z]{2}$/.test(window.Shopify.country || '')) {
        input.buyerIdentity = { countryCode: window.Shopify.country };
      }
      this.handleErrorMessage();
      this.pending = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.buyNowButton.setAttribute('aria-busy', 'true');
      this.buyNowButton.classList.add('loading');
      this.buyNowButton.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.syncBuyNow();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      let navigating = false;
      try {
        // A NEW Storefront API cart; never add, clear or update the online-store cart.
        const response = await fetch('/api/2026-01/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            query: `mutation EspaceBuyNow($input: CartInput!) {
              cartCreate(input: $input) {
                cart { checkoutUrl lines(first: 2) { nodes { quantity merchandise { ... on ProductVariant { id } } } } }
                userErrors { message }
                warnings { message }
              }
            }`,
            variables: { input }
          })
        });
        const result = await response.json();
        const payload = result.data?.cartCreate;
        if (!response.ok || result.errors?.length || !payload) {
          throw new Error('Unable to start a separate checkout. Your shopping cart has not been changed. Please try again.');
        }
        const issue = payload.userErrors?.[0] || payload.warnings?.[0];
        if (issue) throw new Error(issue.message);
        const cart = payload.cart;
        const lines = cart?.lines?.nodes || [];
        if (lines.length !== 1 || lines[0].merchandise?.id !== merchandiseId || lines[0].quantity !== quantity) {
          throw new Error('The selected item or quantity could not be confirmed. Your shopping cart has not been changed.');
        }
        const url = new URL(cart.checkoutUrl);
        if (url.protocol !== 'https:') throw new Error('Invalid checkout address. Please try again.');
        window.location.assign(url.href);
        navigating = true;
        this.navigationTimeout = setTimeout(() => {
          this.pending = false;
          this.submitButton.removeAttribute('aria-disabled');
          this.syncBuyNow();
          this.handleErrorMessage('Checkout is ready. If this page did not redirect, continue using the link below.');
          if (this.errorMessage) {
            const link = document.createElement('a');
            link.href = url.href;
            link.textContent = ' Continue to checkout';
            link.style.textDecoration = 'underline';
            this.errorMessage.appendChild(link);
          }
        }, 10000);
      } catch (error) {
        this.handleErrorMessage(error.name === 'AbortError'
          ? 'Checkout took too long to respond. Your shopping cart has not been changed. Please try again.'
          : (error.message || 'Unable to open checkout. Please try again.'));
      } finally {
        clearTimeout(timeout);
        this.buyNowButton.removeAttribute('aria-busy');
        this.buyNowButton.classList.remove('loading');
        this.buyNowButton.querySelector('.loading__spinner')?.classList.add('hidden');
        if (!navigating) {
          this.pending = false;
          this.submitButton.removeAttribute('aria-disabled');
          this.syncBuyNow();
        }
      }
    }

    addCartRecoveryLink() {
      if (!this.errorMessage) return;
      const link = document.createElement('a');
      link.href = `${window.Shopify.routes.root}cart`;
      link.textContent = ' Open cart';
      link.style.textDecoration = 'underline';
      this.errorMessage.appendChild(link);
    }

    handleErrorMessage(errorMessage = false) {
      // Always expose failures, including on gift-card recipient forms.
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) this.errorMessage.textContent = errorMessage;
    }

    toggleSubmitButton(disable = true, text) {
      this.submitButton.disabled = disable || Boolean(this.uncertain);
      if (disable) {
        if (text) this.submitButtonText.textContent = text;
      } else {
        this.submitButtonText.textContent = window.variantStrings.addToCart;
      }
      this.syncBuyNow();
    }

    get variantIdInput() {
      return this.form.querySelector('[name="id"]');
    }
  });
}
